import { useTranslation } from "react-i18next";
import { X, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@tellescope/ui";
import { formatFileSize } from "@tellescope/utils";
import { FileUploadedItemPreview } from "../FileUploadedItemPreview";
import { FileLocalItemPreview } from "../FileLocalItemPreview";
import { FilePreviewTile } from "../FilePreviewTile";
import { FileFieldItemRow } from "./FileFieldItemRow";
import type { TellescopeFormFieldBaseProps } from "../../types";

type UploadedItem = NonNullable<TellescopeFormFieldBaseProps<"files">["value"]>[number];

interface FileFieldListProps {
  fileListCount: number;
  uploadedFiles: UploadedItem[];
  inFlightFiles: File[];
  inFlightVariant: "uploading" | "pending";
  erroredFiles: File[];
  onRemoveUploaded: (secureName: string) => void;
  onRemoveInFlight: (index: number) => void;
  onRemoveErrored: (index: number) => void;
  onRetryUpload: (file: File, index: number) => void;
}

export const FileFieldList = ({
  fileListCount,
  uploadedFiles,
  inFlightFiles,
  inFlightVariant,
  erroredFiles,
  onRemoveUploaded,
  onRemoveInFlight,
  onRemoveErrored,
  onRetryUpload,
}: FileFieldListProps) => {
  const { t } = useTranslation("forms");

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t("{{count}} file selected", { count: fileListCount })}</p>
      <div className="space-y-1">
        {uploadedFiles.map((file) => (
          <FileFieldItemRow
            key={file.secureName}
            preview={<FileUploadedItemPreview name={file.name} secureName={file.secureName} type={file.type} />}
            title={file.name}
            subtitle={file.type ?? ""}
            actions={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveUploaded(file.secureName);
                }}
                aria-label={t("Remove file")}
                className="flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            }
          />
        ))}
        {inFlightFiles.map((file, i) => (
          <FileFieldItemRow
            key={`${file.name}-${i}`}
            variant={inFlightVariant === "uploading" ? "muted" : undefined}
            preview={<FileLocalItemPreview file={file} />}
            title={file.name}
            subtitle={formatFileSize(file.size)}
            status={
              inFlightVariant === "uploading" ? (
                <p className="text-sm text-muted-foreground">{t("Uploading...")}</p>
              ) : undefined
            }
            actions={
              inFlightVariant === "pending" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveInFlight(i);
                  }}
                  aria-label={t("Remove file")}
                  className="flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : undefined
            }
          />
        ))}
        {erroredFiles.map((file, i) => (
          <FileFieldItemRow
            key={`${file.name}-${i}-error`}
            variant="error"
            preview={<FilePreviewTile icon={AlertTriangle} iconClassName="text-destructive" />}
            title={file.name}
            subtitle={formatFileSize(file.size)}
            actions={
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetryUpload(file, i);
                  }}
                  aria-label={t("Retry upload")}
                  className="flex-shrink-0"
                  color="red"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveErrored(i);
                  }}
                  aria-label={t("Remove file")}
                  className="flex-shrink-0"
                  color="red"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            }
          />
        ))}
      </div>
    </div>
  );
};
